
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Edit, Trash, Globe } from "lucide-react";
import type { Database } from "@/integrations/supabase/types";
import { usePermissions } from "@/hooks/use-permissions";

type UserProfile = Database['public']['Tables']['profiles']['Row'];
type UserRole = Database['public']['Enums']['user_role'];

interface MobileUserCardProps {
  user: UserProfile;
  onEdit: (user: UserProfile) => void;
  onVerify: (user: UserProfile) => void;
  onDelete?: (user: UserProfile) => void;
  onManageCountries?: (user: UserProfile) => void;
}

const MobileUserCard = ({ user, onEdit, onVerify, onDelete, onManageCountries }: MobileUserCardProps) => {
  const { isSuperAdmin } = usePermissions();

  const getRoleBadge = (role: UserRole) => {
    switch (role) {
      case "super_admin":
        return <Badge className="bg-red-500">Super Admin</Badge>;
      case "admin":
        return <Badge className="bg-blue-500">Admin</Badge>;
      case "media":
        return <Badge className="bg-purple-500">Media</Badge>;
      case "pollster":
        return <Badge className="bg-green-500">Pollster</Badge>;
      default:
        return <Badge variant="outline">Public</Badge>;
    }
  };

  const getVerificationBadge = (verified: boolean | null) => {
    if (verified === true) {
      return <Badge className="bg-green-500">Verified</Badge>;
    } else if (verified === false) {
      return <Badge variant="outline">Pending</Badge>;
    }
    return null;
  };

  return (
    <Card className="border border-gray-200 dark:border-gray-700 shadow-sm">
      <CardContent className="p-4">
        <div className="flex flex-col space-y-4">
          {/* User name and email */}
          <div>
            <h3 className="font-medium">{user.name || "Unnamed User"}</h3>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>

          {/* Role and verification status */}
          <div className="flex flex-wrap gap-2">
            {getRoleBadge(user.role)}
            {getVerificationBadge(user.verified)}
          </div>

          {/* Location info */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-500">Country:</span>
              <span className="ml-2">{user.country_code || "—"}</span>
            </div>
            <div>
              <span className="text-gray-500">Constituency:</span>
              <span className="ml-2">{user.constituency_code || "—"}</span>
            </div>
          </div>

          {/* Created date */}
          <div className="text-sm text-gray-500">
            Created: {new Date(user.created_at).toLocaleDateString()}
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 pt-2">
            {isSuperAdmin() && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => onEdit(user)}
                className="flex-1 shadow-sm border-gray-300"
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
            )}
            
            {(user.role === "pollster" || user.role === "media") && 
              !user.verified && (
              <Button 
                variant="outline" 
                size="sm"
                className="flex-1 bg-green-50 text-green-600 hover:bg-green-100 border-green-200 shadow-sm dark:bg-green-900/50 dark:text-green-300 dark:hover:bg-green-800/60 dark:border-green-700"
                onClick={() => onVerify(user)}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Verify
              </Button>
            )}

            {isSuperAdmin() && user.role === "admin" && onManageCountries && (
              <Button 
                variant="outline" 
                size="sm"
                className="flex-1 bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200 shadow-sm dark:bg-blue-900/50 dark:text-blue-300 dark:hover:bg-blue-800/60 dark:border-blue-700"
                onClick={() => onManageCountries(user)}
              >
                <Globe className="h-4 w-4 mr-1" />
                Countries
              </Button>
            )}
            
            {isSuperAdmin() && onDelete && (
              <Button
                variant="destructive"
                size="sm"
                className="flex-1 shadow-sm"
                onClick={() => onDelete(user)}
              >
                <Trash className="h-4 w-4 mr-1" />
                Delete
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MobileUserCard;
